import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CARGO_PROBLEM_SEARCH_MIN,
  type CargoProblemMaterial,
  type CargoProblemMaterialFilter,
} from '@magimp/shared';
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  ReasonModal,
  Seg,
} from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { formatDateTime, type AppLocale } from '../../../i18n/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { useCargoProblemMaterials, useDeleteCargoProblemMaterial } from '../queries.js';

const FILTERS: readonly CargoProblemMaterialFilter[] = ['ALL', 'MEDIA', 'DOCUMENTS'];

export function CargoProblemMaterialLibrary({
  problemId,
  canDelete,
  onJump,
}: {
  problemId: string;
  canDelete: boolean;
  /**
   * Hands the whole material back to the page, which owns the surfaces: a brief
   * file was filed with the work card, everything else with a conversation row.
   */
  onJump: (material: CargoProblemMaterial) => void;
}) {
  const { t, i18n } = useTranslation('cargoProblems');
  const locale = i18n.language as AppLocale;
  const showToast = useToastStore((state) => state.show);
  const [filter, setFilter] = useState<CargoProblemMaterialFilter>('ALL');
  const [search, setSearch] = useState('');
  const [pendingDelete, setPendingDelete] = useState<CargoProblemMaterial | null>(null);
  const remove = useDeleteCargoProblemMaterial(problemId);

  // WHY: the wire contract refuses a shorter needle, so a single keystroke must
  // not travel — it would answer 400 and blank the list.
  const query = useMemo(() => {
    const trimmed = search.trim();
    return trimmed.length >= CARGO_PROBLEM_SEARCH_MIN ? { q: trimmed, filter } : { filter };
  }, [filter, search]);
  const library = useCargoProblemMaterials(problemId, query);

  async function confirmDelete(reason: string) {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync({ fileId: pendingDelete.id, reason });
      showToast({ tone: 'success', text: t('library.deleted') });
    } catch (error) {
      showToast({ tone: 'error', text: errorMessage(error) });
    }
  }

  return (
    <section className="cargo-problem-library" aria-label={t('library.title')}>
      <div className="cargo-problem-library-controls">
        <Seg
          options={FILTERS.map((value) => ({ value, label: t(`library.filters.${value}`) }))}
          value={filter}
          onChange={setFilter}
          aria-label={t('library.title')}
        />
        <Input
          value={search}
          onChange={setSearch}
          icon="search"
          ariaLabel={t('library.search')}
          placeholder={t('library.search')}
          maxLength={200}
        />
      </div>
      {library.isPending ? <LoadingState /> : null}
      {library.isError ? (
        <ErrorState
          text={t('library.error')}
          retryLabel={t('workspace.retry')}
          onRetry={() => void library.refetch()}
        />
      ) : null}
      {library.data && library.data.items.length === 0 ? (
        <EmptyState icon="paperclip" title={t('library.empty')} />
      ) : null}
      {library.data && library.data.items.length > 0 ? (
        <ul className="cargo-problem-library-list">
          {library.data.items.map((file) => (
            <li key={file.id}>
              <div className="cargo-problem-library-meta">
                <span className="cargo-problem-attachment-name">{file.fileName}</span>
                <span>
                  {t('library.uploadedBy', {
                    author: file.uploader.name,
                    date: formatDateTime(file.createdAt, locale),
                  })}
                </span>
                {file.context === 'BRIEF' ? <span>{t('library.brief')}</span> : null}
              </div>
              <div className="cargo-problem-library-actions">
                <Button
                  size="sm"
                  kind="quiet"
                  icon={file.context === 'BRIEF' ? 'file-text' : 'message-square'}
                  onClick={() => onJump(file)}
                >
                  {t(file.context === 'BRIEF' ? 'library.jumpBrief' : 'library.jump')}
                </Button>
                {canDelete ? (
                  <Button
                    size="sm"
                    kind="danger"
                    icon="trash-2"
                    disabled={remove.isPending}
                    onClick={() => setPendingDelete(file)}
                  >
                    {t('library.delete')}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      {pendingDelete ? (
        <ReasonModal
          title={t('library.deleteTitle')}
          text={t('library.deleteText')}
          placeholder={t('library.deleteReason')}
          confirmLabel={t('library.delete')}
          danger
          onConfirm={(reason) => void confirmDelete(reason)}
          onClose={() => setPendingDelete(null)}
        />
      ) : null}
    </section>
  );
}
